import api from "./api";

export interface ApiPage<T> {
  count?: number;
  next?: string | null;
  results: T[];
}

type ListResponse<T> = T[] | ApiPage<T>;
type QueryParams = Record<string, unknown> | undefined;

// The largest page the server gives, so a whole collection loads in as few requests as possible.
const FULL_PAGE = 100;

const apiPath = (url: string) => {
  if (!url.startsWith("http")) return url;
  const parsed = new URL(url);
  return `${parsed.pathname.replace(/^\/api/, "")}${parsed.search}`;
};

/**
 * Load a complete API collection, whether the endpoint returns a plain array
 * or Django REST Framework pagination.
 */
export async function getAll<T>(
  path: string,
  params?: QueryParams,
): Promise<T[]> {
  const results: T[] = [];
  const visited = new Set<string>();
  let next: string | null = path;
  let firstRequest = true;

  while (next && !visited.has(next)) {
    visited.add(next);
    // Annotated rather than inferred: `next` is reassigned from this response
    // and feeds the next request's URL, so leaving the type to inference makes
    // the loop self-referential and TypeScript gives up and calls it `any`.
    const { data }: { data: ListResponse<T> } = await api.get<ListResponse<T>>(
      apiPath(next),
      firstRequest ? { params: { page_size: FULL_PAGE, ...params } } : undefined,
    );
    firstRequest = false;
    if (Array.isArray(data)) {
      results.push(...data);
      break;
    }
    results.push(...data.results);
    next = data.next ?? null;
  }

  return results;
}

/** Load every page while retaining the shape expected by paginated screens. */
export async function getAllPage<T>(
  path: string,
  params?: QueryParams,
): Promise<{ results: T[]; count: number }> {
  const results = await getAll<T>(path, params);
  return { results, count: results.length };
}

/** Load exactly one page for a screen that provides visible page controls. */
export async function getPage<T>(
  path: string,
  params?: QueryParams,
): Promise<{ results: T[]; count: number }> {
  const response = await api.get<ListResponse<T>>(
    path,
    params ? { params } : undefined,
  );
  const data = response.data;
  if (Array.isArray(data)) return { results: data, count: data.length };
  return { results: data.results, count: data.count ?? data.results.length };
}
